import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, Package, XCircle, AlertTriangle, RefreshCw, Clock, Ban } from "lucide-react";
import { toast } from "sonner";
import TrackingTimeline from "../components/TrackingTimeline";
import CancellationTimer, { isCancellable } from "../components/CancellationTimer";
import ConfirmModal from "../components/ConfirmModal";
import ReturnRefundTimeline from "../components/ReturnRefundTimeline";
import { useSettings } from "../contexts/SettingsContext";
import { trackPurchase } from "../lib/metaPixel";
import { pushPurchase } from "../lib/gtmDataLayer";
import SEOHead from "../components/SEOHead";

const STATUS_THEME = {
  paid:           { icon: CheckCircle2, color: "text-brand-success", bg: "bg-brand-success/10", title: "Payment received!", subtitle: "Your order is confirmed and being prepared." },
  pending:        { icon: Clock,        color: "text-brand-turmeric", bg: "bg-brand-turmeric/15", title: "Order placed", subtitle: "We're preparing your order. Pay on delivery." },
  failed:         { icon: XCircle,      color: "text-brand-chili",   bg: "bg-brand-chili/10",   title: "Payment failed", subtitle: "We couldn't process your payment. Don't worry — you can retry below." },
  signature_failed:{ icon: AlertTriangle,color: "text-brand-chili",  bg: "bg-brand-chili/10",   title: "Verification failed", subtitle: "Payment couldn't be verified. Contact support if amount was debited." },
  cancelled:      { icon: AlertTriangle,color: "text-brand-muted",   bg: "bg-brand-subtle",     title: "Payment Failed", subtitle: "You can Retry Again." },
};

export default function OrderConfirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { orderCancellationWindowEnabled, customerCancellationEnabled } = useSettings();
  const [order, setOrder] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [settings, setSettings] = useState({ razorpay_key_id: "" });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Forces a re-render when the cancellation timer reaches zero so the
  // Cancel Order button hides automatically without a page refresh.
  const [, setNowTick] = useState(0);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]);

  // iter52/iter61 — fallback Purchase fire for users who land on
  // /order/<id> directly (bookmark, email link). The PRIMARY fire happens
  // in Checkout.jsx at the moment of order commit (more reliable than
  // waiting for the async GET /orders/<id> here). We share a single
  // sessionStorage flag `purchase_pushed_<order_id>` with the primary
  // path, so:
  //   • Normal checkout → flag set in Checkout → this effect skips ✓
  //   • Direct load of /order/<id> (no Checkout step) → flag NOT set →
  //     this effect fires the push ✓
  //   • Hard refresh of /order/<id> AFTER a successful checkout → flag
  //     still set → skip (the original push already went out from
  //     Checkout before the refresh wiped dataLayer) ✓
  useEffect(() => {
    if (!order) return;
    if (order.order_status === "cancelled" || order.order_status === "payment_failed") return;
    // Only fire when payment is captured OR it's a COD order in "placed" state.
    const cod = order.payment_method === "cod";
    const paid = order.payment_status === "paid";
    if (!cod && !paid) return;
    const key = `purchase_pushed_${order.order_id}`;
    try {
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
    } catch (_) {
      /* sessionStorage may be unavailable in privacy modes — fire anyway */
    }
    trackPurchase(order);
    pushPurchase(order);
  }, [order]);

  useEffect(() => {
    if (document.getElementById("razorpay-script")) return;
    const s = document.createElement("script");
    s.id = "razorpay-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    document.body.appendChild(s);
  }, []);

  const handleCancel = useCallback(async () => {
    if (!order) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/cancel`);
      setOrder(data);
      toast.success("Order cancelled successfully.");
      setCancelModalOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCancelling(false);
    }
  }, [order]);

  const retryPayment = async () => {
    if (!order) return;
    if (!window.Razorpay) { toast.error("Payment gateway is still loading."); return; }
    if (!order.razorpay_order_id) { toast.error("This order has no payment session. Please place a new order."); return; }
    setRetrying(true);
    const options = {
      key: settings.razorpay_key_id,
      amount: Math.round(order.total * 100),
      currency: "INR",
      name: "Sri Durga Foods",
      description: `Retry · ${order.order_id}`,
      order_id: order.razorpay_order_id,
      prefill: { name: order.user_name, email: order.user_email, contact: order.address?.phone || "" },
      notes: { order_id: order.order_id },
      theme: { color: "#E11D24" },
      method: { upi: true, card: true, netbanking: true, wallet: true, paylater: true, emi: true },
      handler: async (response) => {
        try {
          await api.post("/orders/verify-payment", {
            order_id: order.order_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success("Payment successful!");
          const { data } = await api.get(`/orders/${order.order_id}`);
          setOrder(data);
        } catch (err) {
          toast.error(formatApiError(err));
        } finally { setRetrying(false); }
      },
      modal: {
        ondismiss: () => { setRetrying(false); toast("Payment Failed. You can Retry Again."); },
      },
    };
    const rz = new window.Razorpay(options);
    rz.on("payment.failed", (res) => {
      setRetrying(false);
      const err = res?.error || {};
      api.post("/orders/payment-failed", {
        order_id: order.order_id,
        reason: err.description || "Payment failed",
        razorpay_payment_id: err.metadata?.payment_id || "",
        error_code: err.code || "",
        error_description: err.description || "",
      }).catch(() => {});
      toast.error(`Payment failed: ${err.description || "Please try again."}`);
    });
    rz.open();
  };

  if (!order) return <div className="text-center py-32 text-brand-muted">Loading…</div>;

  const status = order.payment_status || "pending";
  const isCancelledStatus = order.order_status === "cancelled";
  const isCustomerCancelled = isCancelledStatus && order.cancelled_by === "customer";
  // Customer cancellation overrides the payment-cancelled (closed-modal) theme.
  // A COD-cancelled order has both payment_status="cancelled" and
  // order_status="cancelled" set, so we explicitly use the order-cancelled hero.
  const theme = isCustomerCancelled
    ? { icon: Ban, color: "text-brand-chili", bg: "bg-brand-chili/10",
        title: "Order cancelled",
        subtitle: "We've cancelled this order on your request. Any payment, if charged, will be refunded within 5–7 business days." }
    : (STATUS_THEME[status] || STATUS_THEME.pending);
  const Icon = theme.icon;
  const canRetry = !isCustomerCancelled
    && (status === "failed" || status === "cancelled" || status === "signature_failed")
    && order.payment_method === "razorpay";
  const cancellable = isCancellable(order, 30, orderCancellationWindowEnabled) && customerCancellationEnabled;

  return (
    <div className="container-x py-12 lg:py-20 max-w-3xl" data-testid="order-confirmation">
      <SEOHead title={`Order ${order.order_id} | Sri Durga Foods`} description="Order confirmation and tracking details for your Sri Durga Foods purchase." noindex />
      <div className="text-center mb-10">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${theme.bg} mb-6`} data-testid="order-status-icon">
          <Icon className={`w-8 h-8 ${theme.color}`} strokeWidth={1.5} />
        </div>
        <span className="overline mb-3 block">{theme.title}</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark mb-3">
          {status === "paid" ? `Thank you, ${order.user_name.split(" ")[0]}.` : theme.title}
        </h1>
        <p className="text-brand-muted">{theme.subtitle}</p>

        {/* Cancellation countdown — visible only when admin has the 30-min window enabled */}
        {cancellable && orderCancellationWindowEnabled && (
          <div
            className="mt-5 inline-flex items-center gap-2 text-sm text-brand-dark bg-white border border-brand-line rounded-full px-4 py-2"
            data-testid="cancel-window-strip"
          >
            <Clock className="w-4 h-4 text-brand-chili" strokeWidth={1.7} />
            <span>If you want to cancel your order, it can be within</span>
            <CancellationTimer
              createdAt={order.created_at}
              onExpire={() => setNowTick((n) => n + 1)}
              testId="cancel-countdown"
            />
          </div>
        )}

        <p className="text-brand-muted text-sm mt-3">Order <span className="font-mono text-brand-dark" data-testid="order-id">{order.order_id}</span></p>

        {canRetry && (
          <button onClick={retryPayment} disabled={retrying} className="btn-primary mt-6 inline-flex items-center" data-testid="retry-payment-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? "animate-spin" : ""}`} strokeWidth={2} />
            {retrying ? "Opening payment…" : `Retry payment · ₹${order.total.toFixed(0)}`}
          </button>
        )}
        {order.payment_failure_reason && (
          <div className="mt-4 text-xs text-brand-chili max-w-md mx-auto" data-testid="payment-failure-reason">
            Reason: {order.payment_failure_reason}
          </div>
        )}
      </div>

      <div className="bg-white border border-brand-line rounded-2xl p-6 sm:p-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-serif text-2xl text-brand-dark">Order summary</h3>
          <span className="text-xs uppercase tracking-wider px-3 py-1 bg-brand-subtle rounded-full" data-testid="order-status">{order.order_status}</span>
        </div>

        <div className="space-y-3 border-t border-brand-line pt-5">
          {order.items.map((it) => (
            <div key={`${it.product_id}__${it.variant_label || ""}`} className="flex justify-between text-sm">
              <span className="text-brand-dark">
                {it.name}
                {it.variant_label ? <span className="text-brand-muted"> · {it.variant_label}</span> : null}
                <span className="text-brand-muted"> × {it.quantity}</span>
              </span>
              <span className="font-medium">₹{it.line_total.toFixed(0)}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-brand-line pt-5 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-brand-muted">Product Total</span><span data-testid="oc-subtotal">₹{order.subtotal.toFixed(0)}</span></div>
          <div className="flex justify-between"><span className="text-brand-muted">Shipping Charge</span><span data-testid="oc-shipping">{order.delivery_charge === 0 ? "Free" : `₹${order.delivery_charge}`}</span></div>
          {/* Session L / iter109 — dynamic charges snapshot preserved
               at order-time.  Old orders (pre-Session-L) show the
               legacy single "Handling Fee" line. */}
          {Array.isArray(order.charges_snapshot) && order.charges_snapshot.length > 0 ? (
            order.charges_snapshot.map((c) => (
              <div key={c.id || c.name} className="flex justify-between" data-testid={`oc-charge-${c.id || c.name}`}>
                <span className="text-brand-muted">{c.name}</span>
                <span>₹{Number(c.amount || 0).toFixed(0)}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-between"><span className="text-brand-muted">Handling Fee</span><span data-testid="oc-handling">₹{Number(order.handling_fee || 0).toFixed(0)}</span></div>
          )}
          <div className="flex justify-between font-serif text-2xl text-brand-dark pt-2 border-t border-brand-line">
            <span>Grand Total</span><span className="text-brand-chili font-semibold" data-testid="oc-grand-total">₹{order.total.toFixed(0)}</span>
          </div>
        </div>

        <div className="border-t border-brand-line pt-5">
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 text-brand-chili mt-0.5" strokeWidth={1.5} />
            <div className="text-sm">
              <div className="font-medium text-brand-dark">Shipping to</div>
              <div className="text-brand-muted mt-1 leading-relaxed">
                {order.address.full_name}<br />
                {order.address.house_no || order.address.line1 || ""}
                {(order.address.street || order.address.line2) ? `, ${order.address.street || order.address.line2}` : ""}
                {order.address.landmark ? ` · ${order.address.landmark}` : ""}<br />
                {order.address.city}, {order.address.state} — {order.address.pincode}<br />
                Phone: {order.address.phone}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-brand-line pt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-brand-muted">
          <div>
            <div className="font-semibold text-brand-dark uppercase tracking-wider text-[10px] mb-1">Payment method</div>
            <div>{order.payment_method?.toUpperCase()}</div>
          </div>
          <div>
            <div className="font-semibold text-brand-dark uppercase tracking-wider text-[10px] mb-1">Payment status</div>
            <div data-testid="payment-status">{status}</div>
          </div>
          {order.razorpay_payment_id && (
            <div className="sm:col-span-2">
              <div className="font-semibold text-brand-dark uppercase tracking-wider text-[10px] mb-1">Razorpay Payment ID</div>
              <div className="font-mono text-[11px]">{order.razorpay_payment_id}</div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8" data-testid="order-tracking-block">
        <TrackingTimeline
          status={order.order_status}
          courier={order.courier_name}
          awb={order.awb_code}
          trackingUrl={order.tracking_url}
          events={order.tracking_events || []}
        />
      </div>

      {/* iter39: Return / Refund timeline — renders only when a return flow is active */}
      <div className="mt-6">
        <ReturnRefundTimeline order={order} testId="order-return-refund-timeline" />
      </div>

      {/* Bottom action bar:
       *   row 1: Continue Shopping (green) + Cancel Order (red) side-by-side
       *   row 2: View All Orders (centered, outline)
       *   When cancellation window has elapsed: Cancel Order is hidden and
       *   Continue Shopping centers itself in row 1.
       */}
      <div className="mt-10 flex flex-col items-center gap-4" data-testid="order-action-bar">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/shop"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full border-2 border-brand-success text-brand-success font-medium tracking-wide bg-transparent active:bg-brand-success/5 [@media(hover:hover)]:hover:bg-brand-success/5 transition-colors"
            data-testid="continue-shopping"
          >
            Continue shopping
          </Link>
          {cancellable && !isCancelledStatus && (
            <button
              type="button"
              onClick={() => setCancelModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-brand-chili text-brand-chili font-medium tracking-wide bg-transparent active:bg-brand-chili/5 [@media(hover:hover)]:hover:bg-brand-chili/5 transition-colors"
              data-testid="cancel-order-btn"
            >
              <Ban className="w-4 h-4" strokeWidth={1.8} />
              Cancel order
            </button>
          )}
        </div>
        <Link
          to="/orders"
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-full border border-brand-muted/40 text-brand-muted text-sm font-medium tracking-wide active:text-brand-dark active:border-brand-dark [@media(hover:hover)]:hover:text-brand-dark [@media(hover:hover)]:hover:border-brand-dark transition-colors"
          data-testid="view-all-orders"
        >
          View all orders
        </Link>
      </div>

      <ConfirmModal
        open={cancelModalOpen}
        title="Cancel this order?"
        message="Are you sure you want to cancel this order? This cannot be undone."
        confirmLabel={cancelling ? "Cancelling…" : "Yes, cancel order"}
        cancelLabel="Keep order"
        confirmDisabled={cancelling}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancel}
        dataTestId="cancel-order-confirm"
      />
    </div>
  );
}
